import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, TrendingUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
    { label: "Artigos", href: "/#artigos", anchor: "artigos" },
    { label: "Manifesto", href: "/#manifesto", anchor: "manifesto" },
    { label: "Calculadora", href: "/calculadora", anchor: null },
    { label: "Ferramentas", href: "/#ferramentas", anchor: "ferramentas" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location]);

    const go = (link) => {
        if (!link.anchor) {
            navigate(link.href);
            return;
        }
        if (location.pathname === "/") {
            document.getElementById(link.anchor)?.scrollIntoView({ behavior: "smooth" });
        } else {
            navigate(link.href);
        }
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${scrolled || open ? "bg-[#060913]/85 backdrop-blur-xl border-white/10" : "bg-[#060913]/60 backdrop-blur-md border-white/5"}`}
        >
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
                <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
                    <span className="flex items-center justify-center w-8 h-8 bg-gold text-ink">
                        <TrendingUp size={18} strokeWidth={2.5} />
                    </span>
                    <span className="font-serif text-lg md:text-xl tracking-tight">
                        Investidor <span className="text-gold italic">Comum</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <button
                            key={l.label}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            onClick={() => go(l)}
                            className="text-sm text-slate-300 hover:text-gold transition-colors tracking-wide"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-newsletter-cta"
                        onClick={() => go({ href: "/#newsletter", anchor: "newsletter" })}
                        className="text-sm font-medium bg-gold text-ink px-5 py-2 hover:bg-golddark transition-colors"
                    >
                        Assinar newsletter
                    </button>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden text-white p-2"
                    onClick={() => setOpen(!open)}
                    aria-label="Abrir menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden overflow-hidden bg-[#060913]/95 backdrop-blur-xl border-t border-white/10"
                    >
                        <div className="px-4 py-6 flex flex-col gap-5">
                            {LINKS.map((l) => (
                                <button
                                    key={l.label}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    onClick={() => go(l)}
                                    className="text-left text-lg text-slate-200 hover:text-gold transition-colors"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="nav-mobile-newsletter-cta"
                                onClick={() => go({ href: "/#newsletter", anchor: "newsletter" })}
                                className="mt-2 text-sm font-medium bg-gold text-ink px-5 py-3 text-center"
                            >
                                Assinar newsletter
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
